import type { MessageContent, UserId } from './protocol/index.js'
import type { ServerFrame } from './protocol/index.js'
import {
  type KeyPair, loadOrCreateKeyPair, exportPublicKey, deriveSharedKey, encrypt, decrypt,
  generateKeyPair, signPrekey, x3dhSend, x3dhReceive, type X3DHBundle,
  loadOrCreateIdentityKeyPair, type IdentityKeyPair,
} from './crypto.js'

// Number of one-time prekeys to generate per upload batch.
const OTP_BATCH_SIZE = 20

/**
 * Per-user E2E session. Supports two modes:
 *
 * LIVE (original): Both parties are online. ECDH P-256 key exchange via the
 * `pubkey`/`peerkey` frames. Instant but requires both parties to be connected.
 *
 * ASYNC (X3DH): The sender encrypts to the recipient's prekey bundle while the
 * recipient is offline. Uses X3DH (Extended Triple DH) with identity keys,
 * signed prekeys, and one-time prekeys. The recipient derives the same shared
 * key from the init message when they come online.
 *
 * Both modes produce an AES-GCM 256 shared key for message encryption.
 */
export class E2ESession {
  // Live ECDH mode state
  private kp?: KeyPair
  private shared?: CryptoKey

  // X3DH async mode state
  private identityKP: IdentityKeyPair | undefined = undefined
  private signedPreKP: KeyPair | undefined = undefined
  private signedPrekeyId: string | undefined = undefined
  private readonly otpKeys: KeyPair[] = []       // one-time prekeys awaiting matching
  private x3dhShared?: CryptoKey
  // Queued init messages arriving before we could derive (shouldn't happen, but safe)
  private pendingX3DH: { senderIK: string; ephemeralKey: string; spkId: string; usedOTP: boolean } | undefined = undefined

  constructor(private readonly storageKey: string) {}

  get ready(): boolean { return !!(this.shared ?? this.x3dhShared) }

  /** Live ECDH mode: Generate/restore our keypair and return our public key to publish. */
  async begin(): Promise<string> {
    this.kp = await loadOrCreateKeyPair(this.storageKey)
    return exportPublicKey(this.kp.publicKey)
  }

  /** Live ECDH mode: A peer published their key — derive the shared secret. */
  async onPeerKey(peerKeyB64: string): Promise<void> {
    if (!this.kp) return
    this.shared = await deriveSharedKey(this.kp.privateKey, peerKeyB64)
  }

  // ── X3DH async mode ────────────────────────────────────────────────────────

  /** X3DH: Generate identity key, signed prekey, and OTP prekeys.
   *  Returns the upload frame payload the caller should send to the server. */
  async initX3DH(): Promise<{
    identityKey: string; signedPrekey: string; signedPrekeyId: string;
    signature: string; oneTimePrekeys: string[]
  }> {
    // Restore or generate persistent identity keypair (ECDH + ECDSA).
    this.identityKP = await loadOrCreateIdentityKeyPair(this.storageKey)
    // Always generate a fresh signed prekey (rotation).
    this.signedPreKP = await generateKeyPair()
    this.signedPrekeyId = `spk-${Date.now()}-${Math.random().toString(36).slice(2)}`
    // Batch of one-time prekeys.
    for (let i = 0; i < OTP_BATCH_SIZE; i++) this.otpKeys.push(await generateKeyPair())

    const signedPrekeyPub = await exportPublicKey(this.signedPreKP.publicKey)
    const signature      = await signPrekey(this.identityKP.ecdsaKP.privateKey, this.signedPreKP.publicKey)
    const oneTimePrekeys = await Promise.all(this.otpKeys.map(kp => exportPublicKey(kp.publicKey)))

    return {
      identityKey:    this.identityKP.publicKeyB64,
      signedPrekey:   signedPrekeyPub,
      signedPrekeyId: this.signedPrekeyId,
      signature,
      oneTimePrekeys,
    }
  }

  /** X3DH sender: given a recipient's prekey bundle, derive the shared key and
   *  return the init message fields to embed in the first encrypted message. */
  async x3dhSendTo(bundle: X3DHBundle): Promise<{ ephemeralKey: string; spkId: string; usedOTP: boolean; senderIK: string }> {
    if (!this.identityKP) this.identityKP = await loadOrCreateIdentityKeyPair(this.storageKey)
    const { sharedKey, ephemeralPublicKey } = await x3dhSend(this.identityKP.ecdhKP, bundle)
    this.x3dhShared = sharedKey
    return { ephemeralKey: ephemeralPublicKey, spkId: bundle.signedPrekeyId, usedOTP: !!bundle.oneTimePrekey, senderIK: this.identityKP.publicKeyB64 }
  }

  /** X3DH recipient: given an init message's sender IK + EK + SPK ID, derive
   *  the shared key. `usedOTP` MUST reflect whether the SENDER actually
   *  included a one-time prekey in its DH computation (carried on the wire
   *  as `x3dhOTP`, see `X3DHInitFields`) — it must never be inferred from
   *  whether we happen to still have OTP keys locally. Popping one
   *  unconditionally was the bug here: our OTP pool almost always has spare
   *  keys (we upload a batch of 20 and only the sender's own choice consumes
   *  one), so we'd derive dh4 against an OTP the sender never included,
   *  producing a shared key that doesn't match the sender's — every
   *  message would come back "🔒 unable to decrypt" — while also burning a
   *  one-time key that was never actually used. */
  async x3dhReceiveFrom(senderIKb64: string, ephemeralKeyB64: string, spkId: string, usedOTP: boolean): Promise<void> {
    if (!this.identityKP || !this.signedPreKP) {
      // Keys not yet initialised — queue for when initX3DH completes.
      this.pendingX3DH = { senderIK: senderIKb64, ephemeralKey: ephemeralKeyB64, spkId, usedOTP }
      return
    }
    // Only consume an OTP when the sender's own message says it used one.
    const otp = usedOTP ? this.otpKeys.shift() : undefined
    this.x3dhShared = await x3dhReceive(this.identityKP.ecdhKP, this.signedPreKP, senderIKb64, ephemeralKeyB64, otp)
    void spkId  // we matched by position; full impl would look up by ID
  }

  /** Flush pending X3DH derivation after initX3DH() completes. */
  async flushPendingX3DH(): Promise<void> {
    if (!this.pendingX3DH) return
    const { senderIK, ephemeralKey, spkId, usedOTP } = this.pendingX3DH
    this.pendingX3DH = undefined
    await this.x3dhReceiveFrom(senderIK, ephemeralKey, spkId, usedOTP)
  }

  /** Encrypt outgoing text into a wire content object. For X3DH init messages,
   *  the caller should pass x3dhInit fields to embed in the content. */
  async sealText(text: string, x3dhInit?: { ephemeralKey: string; spkId: string; senderIK: string; usedOTP: boolean }): Promise<MessageContent> {
    const key = this.x3dhShared ?? this.shared
    if (!key) throw new Error('secure channel not ready')
    const { ct, iv } = await encrypt(key, text)
    return {
      kind: 'text', text: ct, enc: true, iv,
      ...(x3dhInit ? { x3dhEK: x3dhInit.ephemeralKey, x3dhSPK: x3dhInit.spkId, x3dhIK: x3dhInit.senderIK, x3dhOTP: x3dhInit.usedOTP } as never : {}),
    }
  }

  /** Decrypt one content object if it is encrypted (otherwise pass through). */
  private async openContent(content: MessageContent): Promise<MessageContent> {
    if (content.kind !== 'text' || !content.enc || !content.iv) return content
    const key = this.x3dhShared ?? this.shared
    if (!key) return { kind: 'text', text: '🔒 encrypted' }
    try { return { kind: 'text', text: await decrypt(key, content.text, content.iv) } }
    catch { return { kind: 'text', text: '🔒 unable to decrypt' } }
  }

  /** Decrypt any encrypted message content carried by an incoming frame, in place. */
  async openFrame(frame: ServerFrame): Promise<void> {
    if (frame.type === 'message') frame.message.content = await this.openContent(frame.message.content)
    else if (frame.type === 'sync') {
      for (const m of frame.messages) m.content = await this.openContent(m.content)
    }
  }
}

/** X3DH init fields embedded in a text MessageContent (as extra properties).
 *  Present only on the very first message from a sender to an offline peer. */
export interface X3DHInitFields {
  x3dhEK:  string  // sender's ephemeral public key (base64)
  x3dhSPK: string  // recipient's signed prekey ID used
  x3dhIK:  string  // sender's identity public key (base64)
  /** Whether the sender's DH computation included a one-time prekey (dh4).
   *  The receiver MUST honor this exactly — it decides whether to consume
   *  one of its own OTP keys, and doing so when the sender didn't include
   *  one derives a mismatched shared key (see x3dhReceiveFrom). Absent on
   *  messages from a build predating this field: treated as `false`, which
   *  is only correct if that sender also never used an OTP — a fresh E2E
   *  session on both sides (the normal case) is unaffected either way. */
  x3dhOTP: boolean
}

export function extractX3DHInit(content: MessageContent): X3DHInitFields | null {
  if (content.kind !== 'text' || !content.enc) return null
  const c = content as MessageContent & Partial<X3DHInitFields>
  if (!c.x3dhEK || !c.x3dhSPK || !c.x3dhIK) return null
  return { x3dhEK: c.x3dhEK, x3dhSPK: c.x3dhSPK, x3dhIK: c.x3dhIK, x3dhOTP: c.x3dhOTP ?? false }
}

export { type X3DHBundle } from './crypto.js'
export { type UserId }

